import type {
  RoundPublic,
  RoundEpisodePage,
} from "@softmax/observatory-client/types";
import type { RoundEpisodeRow } from "../round-episodes";
import type {
  FeaturedLeagueMatch,
  FeaturedMatchPool,
} from "./featured-match-records";
import { isFullLengthCompletedReplay } from "./replay-eligibility";

// Rounds eligible to be a featured candidate: a round nobody could watch yet
// (or that never ran) has nothing to show.
const featurableRoundStatuses: readonly RoundPublic["status"][] = [
  "running",
  "claimed",
  "completed",
];

// The /v2/rounds filter for the featured-round window. The fetch must ask the
// server for featurable rounds ONLY: fetching the newest N rounds unfiltered
// and discarding the unfeaturable ones client-side lets a streak of N failed
// rounds empty the pool — and the showcase — while older completed rounds
// still hold perfectly watchable replays.
export const featurableRoundStatusQuery = featurableRoundStatuses
  .map((status) => `status=${status}`)
  .join("&");

// The pure half of pool building. The League page's picks live server-side
// now (GET /v2/leagues/{league_id}/featured-match), while /watch still loads
// its own window for its 1st-vs-2nd playlist — so what counts as a poolable
// replay stays stated once, here, for /watch and the tests.
export function featuredPoolFromRoundPages(
  roundPages: readonly {
    readonly round: RoundPublic;
    readonly page: RoundEpisodePage;
  }[],
): FeaturedMatchPool {
  const replays: FeaturedLeagueMatch[] = [];
  let live: FeaturedLeagueMatch | null = null;

  for (const { round, page } of roundPages) {
    const episodes = page.entries;
    // The API no longer computes a total. Use the stable newest-first position.
    const indexByEpisodeId = new Map(
      episodes.map((episode, index) => [episode.id, index]),
    );
    const episodeNumberFor = (episode: RoundEpisodeRow) => {
      const episodeIndex = indexByEpisodeId.get(episode.id);
      return episodeIndex == null ? null : episodeIndex + 1;
    };

    replays.push(
      ...episodes.filter(isFullLengthCompletedReplay).map((episode) => ({
        kind: "replay" as const,
        round,
        episode,
        episodeNumber: episodeNumberFor(episode),
      })),
    );

    if (!live) {
      const liveEpisode = episodes.find(
        (episode) => episode.status === "running" && episode.live_url,
      );

      if (liveEpisode) {
        live = {
          kind: "live",
          round,
          episode: liveEpisode,
          episodeNumber: episodeNumberFor(liveEpisode),
        };
      }
    }
  }

  return { replays, live };
}
